import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Cpu, Building2, Check, X, ArrowUpRight } from "lucide-react";
import { useCopy } from "@/contexts/LanguageContext";
import { DisclaimerBanner } from "@/components/DisclaimerBanner";

const LANE_ICONS = [Users, Cpu, Building2];

const COPY = {
  en: {
    eyebrow: "The journey", h1a: "From passport to disbursement —", h1b: "who does what,", h1c: "at every stage.",
    sub: "Three participants move one MSME opportunity forward. The platform prepares, matches and records; the MSME controls consent; the lender alone decides.",
    lanes: ["MSME", "Platform", "Lender"],
    stages: [
      ["01", "Prepare", ["Completes the 12-section Financial Passport and uploads documents", "Calculates a rules-based readiness band and highlights gaps", "—"]],
      ["02", "Discover", ["Publishes a funding requirement", "Matches it against every active lender policy and scheme rule", "Sees an anonymised opportunity only if its own policy matches"]],
      ["03", "Express interest", ["Reviews and compares indicative terms side-by-side", "Calculates EMI and total cost for each offer", "Sends indicative terms — amount range, rate, tenure, collateral view"]],
      ["04", "Consent", ["Grants lender-specific, revocable consent", "Releases the full credit file and logs every access", "Receives the standardised file and a lender reference number"]],
      ["05", "Appraise", ["Responds to structured queries", "Keeps a timestamped trail of every query and response", "Assigns a branch/RM, verifies, appraises under its own credit policy"]],
      ["06", "Decide & track", ["Accepts sanction, or receives an improvement plan if declined", "Shows the same status to both sides; feeds anonymised analytics", "Records sanction, decline reasons and disbursement"]],
    ],
    decideEyebrow: "Clear boundaries", decideH: "Who decides what",
    decide: [
      ["BharatKosh platform", ["Organises MSME information", "Matches against written lender policies", "Records consent and audit trail", "Reports aggregated insights"], ["Approve or sanction loans", "Set interest rates or fees", "Handle funds or repayments"]],
      ["Bank / NBFC", ["Defines its own credit policy", "Appraises and decides on each case", "Sets pricing, security and terms", "Disburses directly to the MSME"], ["See full data without consent", "Buy mass lead lists"]],
      ["Government", ["Monitors district & sector credit flow", "Tracks scheme utilisation and inclusion", "Configures scheme rules (via admin)"], ["See individual borrower data", "Influence a lender's credit decision"]],
    ],
    does: "Does", doesNot: "Does not",
    dataEyebrow: "Data & consent", dataH: "What a lender sees, and when",
    data: [["Before consent", "District, sector, ticket size, turnover band, readiness band, match %. No name, PAN or contact."], ["After consent", "Full Financial Passport, documents and promoter details — for that one lender only."], ["Always", "Every view and download is audit-logged. Consent can be withdrawn from the Consent Centre."]],
    cta: "Start a Financial Passport",
  },
  hi: {
    eyebrow: "प्रक्रिया", h1a: "पासपोर्ट से वितरण तक —", h1b: "कौन क्या करता है,", h1c: "हर चरण पर।",
    sub: "तीन भागीदार एक MSME अवसर को आगे बढ़ाते हैं। मंच तैयारी, मिलान और रिकॉर्ड करता है; MSME सहमति नियंत्रित करता है; निर्णय केवल ऋणदाता लेता है।",
    lanes: ["MSME", "मंच", "ऋणदाता"],
    stages: [
      ["01", "तैयारी", ["12 खंडों का वित्तीय पासपोर्ट पूरा करता है और दस्तावेज़ अपलोड करता है", "नियम-आधारित तत्परता श्रेणी निकालता है और कमियाँ दिखाता है", "—"]],
      ["02", "खोज", ["ऋण आवश्यकता प्रकाशित करता है", "हर सक्रिय ऋणदाता नीति और योजना नियम से मिलान करता है", "नीति मेल खाने पर ही गुमनाम अवसर देखता है"]],
      ["03", "रुचि व्यक्त करना", ["सांकेतिक शर्तों की आमने-सामने तुलना करता है", "हर प्रस्ताव की EMI और कुल लागत निकालता है", "सांकेतिक शर्तें भेजता है — राशि, दर, अवधि, संपार्श्विक"]],
      ["04", "सहमति", ["ऋणदाता-विशिष्ट, वापस लेने योग्य सहमति देता है", "पूर्ण क्रेडिट फ़ाइल जारी करता है और हर पहुँच दर्ज करता है", "मानकीकृत फ़ाइल और ऋणदाता संदर्भ संख्या प्राप्त करता है"]],
      ["05", "मूल्यांकन", ["संरचित प्रश्नों का उत्तर देता है", "हर प्रश्न और उत्तर का समय-अंकित रिकॉर्ड रखता है", "शाखा/RM नियुक्त कर अपनी क्रेडिट नीति के अनुसार मूल्यांकन करता है"]],
      ["06", "निर्णय एवं ट्रैकिंग", ["स्वीकृति स्वीकार करता है, या अस्वीकृति पर सुधार योजना पाता है", "दोनों पक्षों को समान स्थिति दिखाता है; गुमनाम विश्लेषण देता है", "स्वीकृति, अस्वीकृति के कारण और वितरण दर्ज करता है"]],
    ],
    decideEyebrow: "स्पष्ट सीमाएँ", decideH: "कौन क्या तय करता है",
    decide: [
      ["भारतकोश मंच", ["MSME जानकारी व्यवस्थित करता है", "लिखित ऋणदाता नीतियों से मिलान करता है", "सहमति और ऑडिट रिकॉर्ड रखता है", "समेकित जानकारी देता है"], ["ऋण स्वीकृत करना", "ब्याज दर या शुल्क तय करना", "धन या पुनर्भुगतान संभालना"]],
      ["बैंक / NBFC", ["अपनी क्रेडिट नीति तय करता है", "हर मामले का मूल्यांकन और निर्णय करता है", "मूल्य, प्रतिभूति और शर्तें तय करता है", "सीधे MSME को वितरण करता है"], ["बिना सहमति पूर्ण डेटा देखना", "थोक लीड सूचियाँ खरीदना"]],
      ["सरकार", ["जिला एवं क्षेत्रवार ऋण प्रवाह की निगरानी", "योजना उपयोग और समावेशन की ट्रैकिंग", "योजना नियम निर्धारण (प्रशासक द्वारा)"], ["व्यक्तिगत उधारकर्ता डेटा देखना", "ऋणदाता के निर्णय को प्रभावित करना"]],
    ],
    does: "करता है", doesNot: "नहीं करता",
    dataEyebrow: "डेटा एवं सहमति", dataH: "ऋणदाता क्या और कब देखता है",
    data: [["सहमति से पहले", "जिला, क्षेत्र, ऋण राशि, टर्नओवर श्रेणी, तत्परता श्रेणी, मिलान %। नाम, PAN या संपर्क नहीं।"], ["सहमति के बाद", "पूरा वित्तीय पासपोर्ट, दस्तावेज़ और प्रवर्तक विवरण — केवल उसी एक ऋणदाता के लिए।"], ["हमेशा", "हर दृश्य और डाउनलोड ऑडिट में दर्ज होता है। सहमति, सहमति केंद्र से वापस ली जा सकती है।"]],
    cta: "वित्तीय पासपोर्ट शुरू करें",
  },
};

function StageRow({ s, lanes, i }) {
  const [n, title, cells] = s;
  return (
    <motion.div initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.4, delay: i * 0.04 }}
      data-testid={`how-stage-${n}`} className="grid grid-cols-1 lg:grid-cols-12 gap-3 lg:gap-4 py-6 border-b border-[color:var(--bk-line)]">
      <div className="lg:col-span-3 flex items-baseline gap-3">
        <span className="brand-heading text-3xl text-[color:var(--bk-saffron)] num-tabular">{n}</span>
        <span className="brand-heading text-xl text-[color:var(--bk-navy)]">{title}</span>
      </div>
      {cells.map((c, j) => (
        <div key={j} className={`lg:col-span-3 rounded-xl p-4 text-sm leading-relaxed ${c === "—" ? "text-[color:var(--bk-navy)]/30" : "bg-white border border-[color:var(--bk-line)] text-[color:var(--bk-navy)]/80"}`}>
          <div className="lg:hidden text-[10px] uppercase tracking-widest text-[color:var(--bk-navy)]/50 mb-1">{lanes[j]}</div>{c}
        </div>
      ))}
    </motion.div>
  );
}

function Swimlane({ c }) {
  return (
    <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="how-swimlane">
      <div className="hidden lg:grid grid-cols-12 gap-4 pb-3 border-b-2 border-[color:var(--bk-navy)]">
        <div className="col-span-3" />
        {c.lanes.map((l, i) => {
          const Icon = LANE_ICONS[i];
          return <div key={l} className="col-span-3 flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[color:var(--bk-navy)]"><Icon className="w-4 h-4 text-[color:var(--bk-saffron)]" />{l}</div>;
        })}
      </div>
      {c.stages.map((s, i) => <StageRow key={s[0]} s={s} lanes={c.lanes} i={i} />)}
    </section>
  );
}

function Decide({ c }) {
  return (
    <section className="border-y border-[color:var(--bk-line)]" style={{ background: "var(--bk-ivory-2)" }}>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--bk-navy)]/60">{c.decideEyebrow}</div>
        <h2 className="mt-3 brand-heading text-3xl sm:text-4xl text-[color:var(--bk-navy)]">{c.decideH}</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {c.decide.map(([who, yes, no], i) => (
            <div key={i} data-testid={`decide-${i}`} className="p-6 rounded-2xl bg-white border border-[color:var(--bk-line)]">
              <div className="brand-heading text-xl text-[color:var(--bk-navy)]">{who}</div>
              <div className="mt-4 text-[10px] uppercase tracking-widest text-emerald-700">{c.does}</div>
              <ul className="mt-2 space-y-1.5">{yes.map((y) => <li key={y} className="flex gap-2 text-sm text-[color:var(--bk-navy)]/80"><Check className="w-4 h-4 mt-0.5 text-emerald-600 shrink-0" />{y}</li>)}</ul>
              <div className="mt-4 text-[10px] uppercase tracking-widest text-red-700">{c.doesNot}</div>
              <ul className="mt-2 space-y-1.5">{no.map((y) => <li key={y} className="flex gap-2 text-sm text-[color:var(--bk-navy)]/70"><X className="w-4 h-4 mt-0.5 text-red-500 shrink-0" />{y}</li>)}</ul>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function HowItWorks() {
  const c = useCopy(COPY);
  return (
    <div className="brand-body" data-testid="how-it-works-page">
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-24 pb-6">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--bk-navy)]/60">{c.eyebrow}</div>
        <h1 className="mt-3 brand-heading text-4xl sm:text-5xl lg:text-6xl leading-[1.08] text-[color:var(--bk-navy)] max-w-4xl" data-testid="how-it-works-heading">
          {c.h1a} <em className="italic font-normal text-[color:var(--bk-saffron)]">{c.h1b}</em> {c.h1c}
        </h1>
        <p className="mt-6 text-lg text-[color:var(--bk-navy)]/70 max-w-2xl leading-relaxed">{c.sub}</p>
      </section>
      <Swimlane c={c} />
      <Decide c={c} />
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--bk-navy)]/60">{c.dataEyebrow}</div>
        <h2 className="mt-3 brand-heading text-3xl sm:text-4xl text-[color:var(--bk-navy)]">{c.dataH}</h2>
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {c.data.map(([h, b], i) => (
            <div key={i} data-testid={`data-flow-${i}`} className="p-6 rounded-2xl border border-[color:var(--bk-line)] bg-white">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--bk-saffron)]">{h}</div>
              <p className="mt-3 text-[15px] text-[color:var(--bk-navy)]/80 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
        <Link to="/register" data-testid="how-cta" className="mt-10 btn-primary inline-flex">{c.cta} <ArrowUpRight className="w-4 h-4" /></Link>
      </section>
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-16"><DisclaimerBanner /></div>
    </div>
  );
}
